import { useEffect } from "react";
import StaticPageLayout from "@/components/layout/StaticPageLayout";

const About = () => {
  useEffect(() => {
    document.title = "About | Decision Sales OS";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Decision Sales OS is a decision-first sales operating system built for teams who prioritize execution over administration.");
    }
  }, []);

  return (
    <StaticPageLayout 
      title="About Decision Sales OS"
      description="A decision-first sales operating system built for teams who prioritize execution over administration."
    >
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-foreground mb-4">Our Mission</h2>
        <p className="text-muted-foreground leading-relaxed mb-4">
          Decision Sales OS exists to eliminate the gap between knowing what to do and actually doing it. 
          We believe that sales success comes from consistent, well-timed execution—not from more data, 
          more dashboards, or more complexity.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          Our platform surfaces the right decision at the right time, so sales professionals can focus 
          on what matters: building relationships and closing deals.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-xl font-semibold text-foreground mb-4">What We Build</h2>
        <p className="text-muted-foreground leading-relaxed mb-4">
          Decision Sales OS is not another CRM. It is an operating system designed around daily decisions 
          and weekly execution rhythms. Every feature is built to answer one question: What should I do next?
        </p>
        <ul className="space-y-3 text-muted-foreground">
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
            <span>Decision-first workflows that prioritize action over documentation</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
            <span>Smart lead scoring that surfaces opportunities based on timing and context</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
            <span>Pipeline visibility designed for execution, not just reporting</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
            <span>Calendar integration that connects planning to action</span>
          </li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-xl font-semibold text-foreground mb-4">Our Approach</h2>
        <p className="text-muted-foreground leading-relaxed mb-4">
          We design for clarity. Every screen, every interaction, every notification is evaluated 
          against a simple standard: does this help someone make a better decision faster?
        </p>
        <p className="text-muted-foreground leading-relaxed">
          We believe in tools that respect your time, reduce cognitive load, and get out of the way 
          when the work is done. Decision Sales OS is built to be used, not configured.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-foreground mb-4">Who We Serve</h2>
        <p className="text-muted-foreground leading-relaxed">
          Decision Sales OS is built for sales teams who value execution. Whether you are a solo 
          founder managing your first pipeline or a team lead coordinating across territories, 
          our platform adapts to how you work—not the other way around.
        </p>
      </section>
    </StaticPageLayout>
  );
};

export default About;
